import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasUnsavedChanges } from '@/lib/unsavedChanges';
import { useConfirm } from '@/components/common/useConfirm';

/**
 * Protege los cambios sin guardar en las dos salidas que tiene una pantalla:
 * cerrar la pestaña y navegar dentro de la app.
 *
 * ## Cerrar la pestaña
 *
 * `beforeunload`. El navegador enseña su propio texto —no se puede
 * personalizar— y solo hace falta que el handler cancele el evento.
 *
 * ## Navegar dentro de la app
 *
 * Aquí no sirve `useBlocker` de React Router: **solo existe en el data router**,
 * y el ERP monta `BrowserRouter`. Migrarlo arrastraría las 174 rutas.
 *
 * En su lugar se intercepta el click **en fase de captura**, antes de que el
 * `<Link>` de React Router lo vea. Si hay algo sin guardar se cancela, se
 * pregunta, y solo si el usuario acepta se navega a mano.
 *
 * Lo que se deja pasar sin preguntar, porque no es una navegación de la SPA:
 * clicks con modificador o con el botón central (abren pestaña nueva y **no**
 * sacan al usuario de la página), `target="_blank"`, `download`, enlaces a otro
 * origen, y anclas puras (`#seccion`).
 */
export function useUnsavedChangesGuard() {
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();

  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Cerrar / recargar la pestaña.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!hasUnsavedChanges()) return undefined;
      e.preventDefault();
      // Los navegadores modernos ignoran el texto, pero `returnValue` sigue
      // siendo lo que activa el diálogo en varios de ellos.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Navegación interna por enlace.
  useEffect(() => {
    const onClickCapture = (e) => {
      if (e.defaultPrevented) return;
      // Solo el click primario. El central abre pestaña y no te saca de aquí.
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = typeof e.target?.closest === 'function'
        ? e.target.closest('a[href]')
        : null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      let url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      // Otro origen (o mailto:/tel:) sale de la SPA: que lo gestione el navegador.
      if (url.origin !== window.location.origin) return;
      // Ir a donde ya estás no pierde nada.
      if (url.pathname + url.search === window.location.pathname + window.location.search) return;

      if (!hasUnsavedChanges()) return;

      // A partir de aquí el click es nuestro: hay que pararlo ANTES de que el
      // <Link> de React Router lo procese, y por eso el listener va en captura.
      e.preventDefault();
      e.stopPropagation();

      const destination = url.pathname + url.search + url.hash;
      confirmRef.current({
        title: '¿Salir sin guardar?',
        description: 'Tienes cambios sin guardar en esta pantalla. Si sales ahora se perderán.',
        confirmLabel: 'Salir sin guardar',
        cancelLabel: 'Seguir editando',
        tone: 'destructive',
      }).then((ok) => {
        if (ok) navigateRef.current(destination);
      });
    };

    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, []);

  return { guardDialog: confirmDialog };
}

export default useUnsavedChangesGuard;
